import React, { useState } from 'react';
import { CATEGORIES } from '../constants/inventory';
import Icon from './Icon';
import UnitSelect from './UnitSelect';

export interface CandidateItem {
  name: string;
  quantity: number;
  unit: string;
  category: string;
  expiration_date: string | null;
}

interface Props {
  title: string;
  items: CandidateItem[];
  submitting: boolean;
  onConfirm: (items: CandidateItem[]) => void;
  onCancel: () => void;
}

const InventoryScanReview: React.FC<Props> = ({ title, items: initialItems, submitting, onConfirm, onCancel }) => {
  const [items, setItems] = useState<CandidateItem[]>(initialItems);

  const updateItem = (index: number, patch: Partial<CandidateItem>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onCancel}>✕</button>

        <h2>{title}</h2>
        <p className="scan-review-hint">Review and edit before adding &mdash; nothing is saved yet.</p>

        {items.length === 0 ? (
          <div className="empty-state">
            <Icon name="search" className="icon empty-icon" />
            <h2>Nothing Detected</h2>
            <p>Try a clearer or better-lit photo.</p>
          </div>
        ) : (
          <div className="scan-review-list">
            {items.map((item, index) => (
              <div key={index} className="scan-review-row">
                <button
                  type="button"
                  className="scan-review-remove"
                  onClick={() => removeItem(index)}
                  aria-label={`Remove ${item.name}`}
                >
                  ✕
                </button>

                <div className="form-group">
                  <label>Item Name</label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => updateItem(index, { name: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      step="0.1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, { quantity: parseFloat(e.target.value) || 0 })}
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <UnitSelect value={item.unit} onChange={(unit) => updateItem(index, { unit })} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <select value={item.category} onChange={(e) => updateItem(index, { category: e.target.value })}>
                      <option value="">Select...</option>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Est. Expiration</label>
                    <input
                      type="date"
                      value={item.expiration_date || ''}
                      onChange={(e) => updateItem(index, { expiration_date: e.target.value || null })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={items.length === 0 || submitting}
            onClick={() => onConfirm(items)}
          >
            {submitting ? 'Adding...' : `Add ${items.length || ''} Item${items.length === 1 ? '' : 's'} to Pantry`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryScanReview;
